/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, useCallback, useImperativeHandle, useState} from 'react';
import Box from '@material-ui/core/Box';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {LatLong} from '@ion/core/src/types/entities';
import {TertiaryButton} from '../../components/buttons/TertiaryButton';
import Tooltip from '../../components/tooltip/Tooltip';
import {MaterialExitCloseIcon} from '../../icons/MaterialExitCloseIcon';
import {StarsFillIcon} from '../../icons/StarsFillIcon';
import {StarsIcon} from '../../icons/StarsIcon';
import CardItemRow from '../../layout/CardItemRow';
import {dlsColor, dlsLayout} from '../../theme/theme';
import {BodyTypography} from '../../typography/BodyTypography';
import {LabelTypography} from '../../typography/LabelTypography';
import {SavedPlacesProps} from '../geofences/SearchAddress';
import {FindNearbyType} from '../layers/AssetLayer';
import FindNearby from './findnearby/FindNearby';
import FindNearbyBreadcrumbs from './findnearby/FindNearbyBreadcrumbs';
import ContentLabel from './ContentLabel';

export interface SearchMapLocationPopupType {
  mapView: __esri.MapView;
  address: string;
  place: string;
  openDeleteSavedLocationDialog: (value: SavedPlacesProps) => void;
  isSavedPlace?: boolean;
  savePlace: () => void;
  selectedPlace: SavedPlacesProps;
  isAllowedToSave?: boolean;
  isFindNearbyPopup?: boolean;
  onLocationPopupFindNearby?: Function;
  onFindNearyBy?: Function;
  onFindNearbyBreadcrumbBackClick?: Function,
  onFindNearbyPopupStatusChange?: Function;
  onFindButtonStateChange?: Function;
  findLabel?: FindNearbyType;
  onCloseSearchPopup?: Function;
  setFindNearbyActions?: Function;
  isTrackingV2?: boolean;
}

const useStyles = makeStyles(theme => ({
  toolPopup : {
    margin       : dlsLayout('spacing') * 2,
    marginBottom : dlsLayout('margins')
  },
  value : {
    fontWeight : 700,
    textAlign  : 'right !important'
  },
  closeIcon : {
    cursor : 'pointer'
  },
  breadcrumbs : {
    padding : '10px 20px'
  },
  assetBreadcrumb : {
    cursor     : 'pointer',
    color      : `${dlsColor('grey4')}`,
    fontWeight : '500'
  },
  findNearby : {
    paddingBottom : '10px'
  }
}));

const SearchMapLocationPopup = forwardRef(({
  mapView,
  address,
  place,
  openDeleteSavedLocationDialog,
  savePlace,
  selectedPlace,
  onCloseSearchPopup,
  onLocationPopupFindNearby,
  isSavedPlace = false,
  isFindNearbyPopup,
  onFindNearyBy,
  onFindNearbyPopupStatusChange,
  onFindNearbyBreadcrumbBackClick,
  onFindButtonStateChange,
  setFindNearbyActions,
  findLabel,
  isAllowedToSave = true,
  isTrackingV2 = false
}: SearchMapLocationPopupType, ref) => {
  const classes                                 = useStyles();
  const [showFindNearBy, setShowFindNearBy]     = useState(false);
  const [isShowFilledStar, setIsShowFilledStar] = useState(isSavedPlace);

  function changeToFilledStar() {
    setIsShowFilledStar(true);
  }

  function handleFindNearbyClick() {
    const {center} = mapView;
    const locationCoordinates: LatLong = {
      latitude  : center?.latitude,
      longitude : center?.longitude
    };
    onLocationPopupFindNearby(locationCoordinates);
    setShowFindNearBy(false);
  }

  const showFindNearbyContent = useCallback(function() {
    if (!isTrackingV2) {
      setShowFindNearBy(true);
    } else {
      mapView?.closePopup();
      setFindNearbyActions?.(selectedPlace);
    }
  }, [isTrackingV2, setFindNearbyActions, selectedPlace]);

  const hideFindNearbyContent = useCallback(function() {
    onFindNearbyBreadcrumbBackClick();
    setShowFindNearBy(false);
  }, []);

  useImperativeHandle(ref, () => {
    return {
      savePlaceToPopup : changeToFilledStar
    };
  });

  if (!showFindNearBy) {
    return (
      <>
        <div className={classes.toolPopup}>
          <Box display='flex'>
            <Box flexGrow={1}>
              <Box display='flex' alignItems='center'>
                <BodyTypography>{place}</BodyTypography>
                {isAllowedToSave && (
                  <Box display='flex'
                    alignItems='center'
                    style={{paddingLeft: 4}}
                    onClick={function() {
                      if (!isShowFilledStar) {
                        savePlace();
                        setIsShowFilledStar(true);
                      }
                    }}>
                    {isShowFilledStar && (
                      <Tooltip title={`${translate('maps:searchMap.removeFromSaved')}`}
                        arrow>
                        <div onClick={function() {
                          openDeleteSavedLocationDialog(selectedPlace);
                        }}>
                          <StarsFillIcon />
                        </div>
                      </Tooltip>
                    )}
                    {!isShowFilledStar && <StarsIcon />}
                  </Box>
                )}
              </Box>
            </Box>
            <Box className={classes.closeIcon} onClick={function() {
              onCloseSearchPopup();
              mapView.graphics.removeAll();
              mapView.popup.close();
            }}>
              <MaterialExitCloseIcon />
            </Box>
          </Box>
          <div style={{
            marginTop : dlsLayout('spacing') * 2
          }}>
            <CardItemRow label={
              <ContentLabel label={`${translate('maps:popup.location')}:`}
                isLightColor />
            } value={
              <LabelTypography variantType='label2' className={classes.value}>
                {address}
              </LabelTypography>
            } />
            {!!onLocationPopupFindNearby && (
              <Box display='flex' justifyContent='flex-start'>
                <TertiaryButton noUnderline
                  noUnderlineOnFocus
                  usecaactive
                  style={{minWidth: 0}}
                  onClick={showFindNearbyContent}>
                  {translate('maps:findNearby.header')}
                </TertiaryButton>
              </Box>
            )}
          </div>
        </div>
      </>
    );
  }
  return (
    <div className={classes.findNearby}>
      <FindNearbyBreadcrumbs label={place}
        fallbackLabel={'LOCATION'}
        onBackClick={hideFindNearbyContent}/>
      <FindNearby onFindNearyBy={onFindNearyBy}
        onFindNearbyPopupStatusChange={onFindNearbyPopupStatusChange}
        onFindButtonStateChange={onFindButtonStateChange}
        onFindButtonClick={handleFindNearbyClick}
        showFindNearByPopup={isFindNearbyPopup}
        assetData={selectedPlace}
        geofenceCoordinates={findLabel.geofence}
        locationCoordinates={findLabel.location}
        typeToFindBy={findLabel.type} />
    </div>
  );
});

export default SearchMapLocationPopup;
